import { type AxiosError } from 'axios'
import { useMutation, type UseMutationOptions } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { externalIntegrationsMutationKeys } from 'queries/external-integrations/query-keys'
import {
  type AuthenticationRequestBody,
  type CreateAuthenticationRequestParams
} from 'queries/external-integrations/types'
import { getEndpointWithParams } from 'queries/queries-utils'
import { type TeamExternalIntegrationResponse } from 'types/api/status-checks/status-check'

function createAuthentication([endpoint, endpointParams]: ReturnType<
  typeof externalIntegrationsMutationKeys.createAuthentication
>) {
  return async function (
    args: [CreateAuthenticationRequestParams, AuthenticationRequestBody]
  ) {
    const [requestParams, requestBody] = args
    const endpointWithParams = getEndpointWithParams(endpoint, {
      ...endpointParams,
      ...requestParams
    })
    const response = await agent.post<TeamExternalIntegrationResponse>(
      endpointWithParams,
      requestBody
    )

    return response.data
  }
}

export function useCreateAuthentication(
  options?: UseMutationOptions<
    TeamExternalIntegrationResponse,
    AxiosError,
    [CreateAuthenticationRequestParams, AuthenticationRequestBody]
  >
) {
  const { currentTeamId: teamId } = useAuth()
  const mutationKey =
    externalIntegrationsMutationKeys.createAuthentication(teamId)

  return useMutation({
    ...options,
    mutationFn: createAuthentication(mutationKey),
    mutationKey
  })
}
